/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ROLE_OPTIONS } from './devConstants';

/** Griglia hot-swap ruoli + conteggio utenti nel DB. */
export const RoleSwitcher = ({ busy, status, onImpersonate }) => (
  <>
    <div className="text-[11px] text-muted-foreground leading-snug">
      Sostituisci l'utente attivo con un demo del ruolo scelto (nuovo cookie di auth · TTL 15 min).
    </div>
    <div className="grid grid-cols-2 gap-2">
      {ROLE_OPTIONS.map(({ key, label, icon: Icon, color }) => (
        <button
          key={key}
          type="button"
          disabled={busy}
          onClick={() => onImpersonate(key)}
          className={`rounded-lg border p-2.5 text-left transition-all hover:shadow-md hover:-translate-y-0.5 disabled:opacity-50 disabled:pointer-events-none ${color}`}
          data-testid={`impersonate-${key}`}
        >
          <Icon className="h-4 w-4 mb-1" />
          <div className="text-xs font-bold">{label}</div>
          <div className="text-[10px] opacity-70">Hot-swap 1-click</div>
        </button>
      ))}
    </div>
    {status && (
      <div className="rounded-md bg-muted/50 p-2 text-[10px] font-mono space-y-0.5">
        <div className="font-bold uppercase text-[9px] text-muted-foreground">Utenti nel DB</div>
        {Object.entries(status.users_by_role || {}).map(([k, v]) => (
          <div key={k} className="flex justify-between"><span>{k}</span><span>{v}</span></div>
        ))}
      </div>
    )}
  </>
);

/** Sequenze preset di impersonation (run / salva / rimuovi). */
export const ChainsSection = ({ chains, busy, runningChainId, onRun, onSaveCurrent, onRemove }) => (
  <section className="rounded-lg border border-border p-3 space-y-2">
    <div className="flex items-center justify-between gap-2">
      <div className="text-xs font-bold flex items-center gap-1.5">🔗 Sequenze preset</div>
      <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2" onClick={onSaveCurrent} data-testid="chain-save-current">+ Salva corrente</Button>
    </div>
    <div className="space-y-1.5">
      {chains.map((chain) => (
        <div key={chain.id} className="rounded-md border border-border/60 p-2 flex items-center gap-2" data-testid={`chain-${chain.id}`}>
          <span className="text-base leading-none" aria-hidden="true">{chain.icon}</span>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold truncate">{chain.name}</div>
            <div className="text-[10px] text-muted-foreground truncate">{chain.steps.map((s) => s.role).join(' → ')}</div>
          </div>
          <Button size="sm" className="h-6 px-2 text-[10px]" onClick={() => onRun(chain)} disabled={busy || runningChainId === chain.id} data-testid={`chain-run-${chain.id}`}>
            {runningChainId === chain.id ? '…' : 'Run'}
          </Button>
          {chain.id.startsWith('custom-') && (
            <button type="button" className="text-muted-foreground hover:text-destructive p-0.5" onClick={() => onRemove(chain.id)} aria-label="Rimuovi sequenza">
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </div>
      ))}
    </div>
  </section>
);

const DiffDelta = ({ left, right }) => {
  const keys = Array.from(new Set([...Object.keys(left), ...Object.keys(right)]));
  return (
    <div className="rounded-md bg-primary/5 border border-primary/30 p-2" data-testid="diff-result">
      <div className="text-[10px] font-bold uppercase tracking-wider mb-1">Delta</div>
      <div className="text-[10px] font-mono space-y-0.5">
        {keys.map((k) => {
          const same = JSON.stringify(left[k]) === JSON.stringify(right[k]);
          return (
            <div key={k} className={`grid grid-cols-[80px_1fr_1fr] gap-1 ${same ? 'opacity-40' : 'text-primary font-bold'}`}>
              <span className="truncate">{k}</span>
              <span className="truncate">{String(left[k])}</span>
              <span className="truncate">{String(right[k])}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/** Confronto permessi/quota tra due snapshot di ruoli. */
export const DiffPlayground = ({ busy, diffLeft, diffRight, setDiffLeft, setDiffRight, onCapture }) => {
  const slots = [
    { side: 'left', slot: diffLeft, setSlot: setDiffLeft },
    { side: 'right', slot: diffRight, setSlot: setDiffRight },
  ];
  return (
    <section className="rounded-lg border border-border p-3 space-y-2" data-testid="diff-playground">
      <div className="text-xs font-bold flex items-center gap-1.5">🔬 Diff Playground</div>
      <div className="text-[10px] text-muted-foreground">Confronta permessi, quota e sconti tra due utenti impersonati.</div>
      <div className="grid grid-cols-2 gap-2">
        {slots.map(({ side, slot, setSlot }) => (
          <div key={side} className="rounded-md border border-border/50 bg-muted/30 p-1.5 space-y-1">
            <select
              className="w-full h-6 text-[10px] rounded bg-background border border-input px-1"
              value={slot?.role || ''}
              onChange={(e) => setSlot((v) => ({ ...(v || {}), role: e.target.value }))}
              data-testid={`diff-select-${side}`}
            >
              <option value="">— ruolo —</option>
              {ROLE_OPTIONS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
            <Button size="sm" className="w-full h-6 text-[10px] px-1" onClick={() => onCapture(side)} disabled={!slot?.role || busy} data-testid={`diff-capture-${side}`}>Cattura</Button>
            {slot?.snapshot && (
              <pre className="text-[9px] font-mono bg-background/60 rounded p-1 overflow-auto max-h-32 leading-tight" data-testid={`diff-snapshot-${side}`}>{JSON.stringify(slot.snapshot, null, 1)}</pre>
            )}
          </div>
        ))}
      </div>
      {diffLeft?.snapshot && diffRight?.snapshot && <DiffDelta left={diffLeft.snapshot} right={diffRight.snapshot} />}
    </section>
  );
};
